import { onMount, createEffect, createSignal, batch, For, Show, onCleanup, untrack, createMemo, type Component } from "solid-js";
import { useNavigate } from "@solidjs/router";

import ProbabilityHistoryPageSettings from "../../../../../components/menus/ProbabilityHistoryPageSettings";
import ScatterHistoryColorLegend from "../../../../../components/charts/ScatterHistoryColorLegend";
import Histogram from "../../../../../components/charts/Histogram";
import Probability from "../../../../../components/charts/Probability";
import LoadingOverlay from "../../../../../components/utilities/Loading";
import FleetSourcesGate from "../../../../../components/utilities/FleetSourcesGate";
import DataNotFoundMessage from "../../../../../components/utilities/DataNotFoundMessage";

import { user } from "../../../../../store/userStore";
import { createPerformanceChartsScrollRestore } from "../../../../../utils/performanceChartsScrollRestore";
import {
  registerSelectionStoreCleanup,
  selectedRange,
  selectedRanges,
  selectedEvents,
  cutEvents,
  hasSelection,
  isCut,
  clearSelection,
  cutSelection,
  setSelectedRange,
  setSelectedEvents,
  setSelectedRanges,
  setHasSelection,
  setIsCut,
  setTriggerSelection,
} from "../../../../../store/selectionStore";
import {
  selectedStatesTimeseries,
  setSelectedStatesTimeseries,
  selectedRacesTimeseries,
  setSelectedRacesTimeseries,
  selectedLegsTimeseries,
  setSelectedLegsTimeseries,
  selectedGradesTimeseries,
  setSelectedGradesTimeseries,
  raceOptions,
  setRaceOptions,
  legOptions,
  setLegOptions,
  gradeOptions,
  setGradeOptions,
  setHasChartsWithOwnFilters,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  ensureScatterHistoryDefaultDateRange,
  selectedSources as filterStoreSelectedSources,
  setSelectedSources as filterStoreSetSelectedSources,
  maneuverTackEntry,
  setManeuverTackEntry,
} from "../../../../../store/filterStore";

import { persistantStore } from "../../../../../store/persistantStore";
import { sourcesStore } from "../../../../../store/sourcesStore";
import { error as logError, warn, debug, log } from "../../../../../utils/console";
import { apiEndpoints } from "@config/env";
const { selectedClassName, selectedProjectId, selectedPage, selectedDate, selectedYear, selectedEvent, colorType, setColorType, filterChartsBySelection, setSelectedMenu } = persistantStore;

import { getData, setupMediaContainerScaling } from "../../../../../utils/global";
import { logPageLoad } from "../../../../../utils/logging";
import { ensureFleetSourcesContextReady, createFleetSourcesReadyMemo } from "../../../../../utils/fleetSourcesReady";
import { applyDataFilter, extractFilterOptions } from "../../../../../utils/dataFiltering";
import {
  applyScatterHistoryChartFilters,
  applyScatterHistoryGlobalManeuverFilters,
  applyScatterHistorySelectionCutFilter,
  applyScatterHistoryMetadataFilters,
  filterRowsToLiveActiveWindow,
} from "../../../../../utils/scatterHistoryChartFilters";
import { splitMetadataFilterTokens } from "../../../../../utils/metadataFilterTokens";
import { getLiveQueryDateYmd } from "../../../../../utils/liveSessionDate";
import { computeMapActiveTimeWindow } from "../../../../../utils/fleetTimeWindow";
import { ensureLiveDatasetsForProject } from "../../../../../utils/liveDatasets";
import { timeWindow, getLiveDisplayTargetMs } from "../../../../../store/playbackStore";
import { unifiedDataStore } from "../../../../../store/unifiedDataStore";
import UnifiedFilterService from "../../../../../services/unifiedFilterService";
import { getChartLayoutClass } from "../../../../../utils/chartLayoutUtils";
import {
  colorTypesForDataMode,
  isExploreHistoryColorTypeValid,
  normalizeExploreHistoryPointFields,
} from "../../../../../utils/exploreHistoryColorGroups";
import {
  buildScatterHistoryColorLegendItems,
  collectScatterPointIdsForLegendClick,
  toggleIdsInSet,
  getChartLegendLabelForScatterHistory,
  type ScatterLegendHighlightState,
} from "../../../../../utils/scatterHistoryColorLegend";
import { runWithScatterSelectionBusyCursor } from "../../../../../utils/scatterSelectionBusyCursor";
import { collectDatetimeRangesFromPointIds } from "../../../../../utils/scatterCutFilter";
import { defaultChannelsStore } from "../../../../../store/defaultChannelsStore";
import { useChartProgress } from "../../../../../utils/useChartProgress";
import {
  applyUniformDataResampleToCharts,
  buildExploreResampleFetchPlan,
  stripFleetChannelSuffix,
  isDatetimeLikeExploreAxis,
  DEFAULT_TIMESERIES_DATA_RESAMPLE,
  normalizeTimeseriesDataResample,
  exploreChartsNeedUPlotRenderer,
  type ExploreResampleFetchPlan,
  type TimeseriesDataResample,
} from "../../../../../utils/timeseriesSeriesTransforms";
import {
  fetchProbabilityHistoryFileRows,
  fetchProbabilityHistoryAggregatesAll,
  fetchProbabilityHistoryManeuversAll,
  fetchProbabilityHistoryStartStatsAll,
  normalizeApiDate,
  type ProbabilityHistoryDataMode,
} from "../../../../../services/probabilityHistoryDataService";
import { loadSelectedSources } from "../../../../../services/selectedSourcesService";
import { initializeSourceSelections } from "../../../../../utils/sourceInitialization";
import {
  collectChannelRefsFromCharts,
  getRowChannelValue,
  resolveAxisDataColumnKey,
} from "../../../../../utils/channelRef";

function toNumericAxis(v: unknown): number | undefined {
  if (v === undefined || v === null || v === "") {return undefined;}
  if (typeof v === "number" && Number.isFinite(v)) {return v;}
  if (v instanceof Date && !isNaN(v.getTime())) {return v.getTime();}
  const s = String(v).trim();
  if (!s) {return undefined;}
  const n = Number(s);
  if (Number.isFinite(n)) {return n;}
  const t = Date.parse(s);
  return Number.isNaN(t) ? undefined : t;
}

interface ProbabilityHistoryPageProps {
  [key: string]: unknown;
}

const CHART_TYPE_YAXIS_NAMES = new Set([
  "Histogram",
  "Standard Probability",
  "Categorical Probability",
  "Probability",
  "Cumulative Probability",
]);

function isChartTypeYAxis(name: string | undefined | null): boolean {
  if (!name) {return true;}
  return CHART_TYPE_YAXIS_NAMES.has(String(name).trim());
}

interface ChartChildProps {
  chart: unknown;
  colortype: string;
  class_name: string;
  onReady?: () => void;
  handleZoom?: (info: unknown) => void;
  zoom?: boolean;
  [key: string]: unknown;
}

const getChartComponent = (chart: unknown): Component<ChartChildProps> => {
  const chartType = chart?.series?.[0]?.yaxis?.name || "Standard Probability";
  if (chartType === "Histogram") {return Histogram as Component<ChartChildProps>;}
  return Probability as Component<ChartChildProps>;
};

const PARENT = "probability_history";

/**
 * Saved objects should include `dataMode`. Older JSON may omit it; infer from per-chart fields so we
 * hit the right API (maneuver_stats = simple SQL, dataset_aggregates = events_aggregate / cloud).
 */
function resolveProbabilityHistoryDataMode(
  raw: Record<string, unknown> | null,
  chartsData: unknown[]
): ProbabilityHistoryDataMode {
  const dm = raw?.dataMode;
  if (dm === "server_file" || dm === "dataset_aggregates" || dm === "maneuver_stats" || dm === "start_stats") {
    return dm;
  }
  const charts = Array.isArray(chartsData) ? chartsData : [];
  const anyManeuver = charts.some(
    (ch) => typeof ch?.maneuverEventType === "string" && ch.maneuverEventType.trim() !== ""
  );
  if (anyManeuver) {return "maneuver_stats";}
  const anyAgg = charts.some(
    (ch) => typeof ch?.aggregateAgrType === "string" && String(ch.aggregateAgrType).trim() !== ""
  );
  if (anyAgg) {return "dataset_aggregates";}
  return "server_file";
}

export default function ProbabilityHistoryPage(props: ProbabilityHistoryPageProps) {
  let navigate: (path: string) => void;
  try {
    navigate = useNavigate();
  } catch {
    navigate = () => { };
  }

  const objectName = (): string => (props?.objectName as string) || selectedPage() || "default";

  /** Live mode: read the live session parquet for the active playback window (no date-range picker). */
  const isLive = (): boolean => (props as Record<string, unknown>)?.live === true;

  /**
   * Fleet day mode (day/explore PROBABILITY): this same page pinned to the day being viewed, reading
   * the `fleet_probability` objects the fleet Probability builder writes. Raw parquet rows only — the
   * SQL aggregate/maneuver modes stay with the project-wide history page.
   */
  const isFleetDay = (): boolean => (props as Record<string, unknown>)?.fleetDay === true;
  /** Fleet day charts come from the fleet Probability builder, so they live under a different parent. */
  const parentName = (): string => (isFleetDay() ? "fleet_probability" : PARENT);
  const builderRoute = (): string =>
    isFleetDay() ? "/probability-builder" : "/probability-history-builder";
  const builderTarget = (): string =>
    isFleetDay()
      ? `/probability-builder?object_name=${encodeURIComponent(objectName())}&fleet=true`
      : `/probability-history-builder?object_name=${encodeURIComponent(objectName())}${isLive() ? "&live=1" : ""}`;

  let mediaContainerRef: HTMLElement | undefined;

  registerSelectionStoreCleanup();

  const [charts, setCharts] = createSignal<unknown[]>([]);
  const [originalCharts, setOriginalCharts] = createSignal<unknown[]>([]);
  const [columns, setColumns] = createSignal(0);
  const chartLayoutClass = () => getChartLayoutClass(charts().length, "default", columns());
  const layoutClass = () => {
    const base = chartLayoutClass();
    return charts().length === 1 ? `${base} single-chart` : base;
  };
  const gridPaddingTop = () => {
    const count = charts().length;
    if (count === 1) {return undefined;}
    return count < 4 ? "15vh" : "50px";
  };
  const [isInitializing, setIsInitializing] = createSignal(true);
  const fleetSourcesReady = createFleetSourcesReadyMemo();
  const [zoom, setZoom] = createSignal(false);
  let performanceChartsScrollInnerEl: HTMLElement | undefined;
  const plotScrollRestore = createPerformanceChartsScrollRestore({
    getZoom: zoom,
    getScrollInner: () => performanceChartsScrollInnerEl,
  });
  const [selectedChart, setSelectedChart] = createSignal<unknown>(null);
  const [chartsReadyCount, setChartsReadyCount] = createSignal(0);
  const [hasChartConfigButNoData, setHasChartConfigButNoData] = createSignal(false);
  const [dataMode, setDataMode] = createSignal<ProbabilityHistoryDataMode>("server_file");
  const [savedAggregateEventType, setSavedAggregateEventType] = createSignal("bin 10");
  const [savedAggregateAgrType, setSavedAggregateAgrType] = createSignal("avg");
  const [savedManeuverEventType, setSavedManeuverEventType] = createSignal("tack");
  const [fileDataResample, setFileDataResample] = createSignal<TimeseriesDataResample>(DEFAULT_TIMESERIES_DATA_RESAMPLE);
  const [sharedSelectedPointIds, setSharedSelectedPointIds] = createSignal<Set<number>>(new Set());
  const [cutPointIds, setCutPointIds] = createSignal<Set<number>>(new Set());
  let lastSelectionFilterKey: string | null = null;

  const progress = useChartProgress({
    chartType: "probability",
    className: selectedClassName,
    sourceId: () => 0,
  });

  const colortypes = createMemo(() => colorTypesForDataMode(dataMode()));
  const twaFilterOptions = ["Upwind", "Downwind", "Reaching", "Port", "Stbd"];
  const FOILING_STATE_OPTIONS = ["H0", "H1", "H2"];
  const META_FILTER_SESSION_KEY = "probabilityHistoryMetadataFilters";

  // Year/Event/Config (contains-match tokens) + foiling State — committed on settings Apply.
  const [filterYear, setFilterYear] = createSignal("");
  const [filterEvent, setFilterEvent] = createSignal("");
  const [filterConfig, setFilterConfig] = createSignal("");
  const [filterFoilingStates, setFilterFoilingStates] = createSignal<string[]>([]);

  onMount(() => {
    try {
      const raw = sessionStorage.getItem(META_FILTER_SESSION_KEY);
      if (raw) {
        const o = JSON.parse(raw);
        if (typeof o.year === "string") {setFilterYear(o.year);}
        if (typeof o.event === "string") {setFilterEvent(o.event);}
        if (typeof o.config === "string") {setFilterConfig(o.config);}
        if (Array.isArray(o.states)) {setFilterFoilingStates(o.states.map((s: unknown) => String(s)));}
      }
    } catch {
      /* ignore malformed session state */
    }
  });

  createEffect(() => {
    const payload = {
      year: filterYear(),
      event: filterEvent(),
      config: filterConfig(),
      states: filterFoilingStates(),
    };
    try {
      sessionStorage.setItem(META_FILTER_SESSION_KEY, JSON.stringify(payload));
    } catch {
      /* ignore quota / unavailable sessionStorage */
    }
  });

  /** SQL-mode `filters` JSON: YEAR/EVENT/CONFIG (contains) + STATE (foiling H0/H1/H2). */
  const buildMetadataFilters = (): Record<string, unknown[]> | undefined => {
    const f: Record<string, unknown[]> = {};
    const years = splitMetadataFilterTokens(filterYear());
    if (years.length > 0) {f.YEAR = years;}
    const events = splitMetadataFilterTokens(filterEvent());
    if (events.length > 0) {f.EVENT = events;}
    const configs = splitMetadataFilterTokens(filterConfig());
    if (configs.length > 0) {f.CONFIG = configs;}
    const states = filterFoilingStates()
      .map((s) => String(s).trim().toUpperCase())
      .filter((s) => s === "H0" || s === "H1" || s === "H2");
    if (states.length > 0) {f.STATE = states;}
    return Object.keys(f).length > 0 ? f : undefined;
  };

  createEffect(() => {
    const mode = dataMode();
    const ct = colorType();
    if (!isExploreHistoryColorTypeValid(ct, mode)) {
      setColorType("DEFAULT");
    }
  });

  let abortController: AbortController | null = new AbortController();
  let cleanupScaling: (() => void) | null = null;
  /** Apply saved scatter JSON dates/resample only when switching chart object (not on every reload). */
  let probabilityMetaAppliedForObject: string | null = null;
  /** Incremented each time initializeCharts starts; finally blocks only clear global state when still current. */
  let initGeneration = 0;

  const selectedSourceIds = createMemo(() => {
    const names = filterStoreSelectedSources();
    if (!sourcesStore.isReady() || !Array.isArray(names)) {return new Set<number>();}
    const sources = sourcesStore.sources();
    const ids = new Set<number>();
    for (const name of names) {
      const n = String(name).trim().toLowerCase();
      const s = sources.find((x) => String(x.source_name).trim().toLowerCase() === n);
      if (s && Number.isFinite(Number(s.source_id))) {ids.add(Number(s.source_id));}
    }
    return ids;
  });

  const dataSourcesOptions = createMemo(() => {
    if (!sourcesStore.isReady()) {return [];}
    const projectSources = sourcesStore.sources();
    if (!projectSources?.length) {return [];}
    return projectSources
      .map((s) => {
        const id = Number(s.source_id);
        if (!Number.isFinite(id)) {return null;}
        const name = s.source_name || "";
        const getter = () => {
          const arr = filterStoreSelectedSources();
          return Array.isArray(arr) && arr.some((n) => String(n).trim().toLowerCase() === name.trim().toLowerCase());
        };
        const setter = (value: boolean) => {
          const current = filterStoreSelectedSources();
          const list = Array.isArray(current) ? [...current] : [];
          const norm = name.trim().toLowerCase();
          const idx = list.findIndex((n) => String(n).trim().toLowerCase() === norm);
          if (value) {
            if (idx === -1) {list.push(name);}
          } else if (idx !== -1) {list.splice(idx, 1);}
          filterStoreSetSelectedSources(list);
        };
        return {
          key: `source-${id}`,
          label: name || `Source ${id}`,
          type: "toggle" as const,
          signal: [getter, setter] as [() => boolean, (value: boolean) => void],
        };
      })
      .filter((x): x is NonNullable<typeof x> => x != null);
  });

  const selectedSourceNamesForApi = createMemo((): string[] => {
    const names = filterStoreSelectedSources();
    const sources = sourcesStore.sources();
    const catalog = (sources || []).map((s) => String(s.source_name || "").trim()).filter(Boolean);
    const allowed = new Set(catalog.map((n) => n.toLowerCase()));
    if (!Array.isArray(names) || names.length === 0) {
      return catalog;
    }
    const picked = names.map((n) => String(n).trim()).filter((n) => n && allowed.has(n.toLowerCase()));
    return picked.length > 0 ? picked : catalog;
  });

  createEffect(() => {
    const u = user();
    const cn = selectedClassName();
    const pid = selectedProjectId();
    if (!u?.user_id || !cn || !pid) {return;}
    const uid = u.user_id;
    let cancelled = false;
    onCleanup(() => {
      cancelled = true;
    });
    void (async () => {
      const savedNames = await loadSelectedSources(cn, Number(pid), uid);
      let wait = 0;
      while (!sourcesStore.isReady() && wait < 15) {
        await new Promise((r) => setTimeout(r, 100));
        wait++;
      }
      if (cancelled) {return;}
      const catalog = sourcesStore.sources();
      const catalogNames = (catalog || []).map((s) => String(s.source_name ?? "").trim()).filter(Boolean);
      const allowed = new Set(catalogNames.map((n) => n.toLowerCase()));

      let next: string[] = [];
      if (savedNames && savedNames.length > 0) {
        next = savedNames.filter((n) => allowed.has(n.toLowerCase()));
      }
      if (next.length === 0 && catalogNames.length > 0) {
        next = await initializeSourceSelections();
      }
      if (cancelled) {return;}
      filterStoreSetSelectedSources(next);
      debug("ProbabilityHistory: Applied fleet source selection for project", {
        projectId: pid,
        className: cn,
        count: next.length,
      });
    })();
  });

  const fetchCharts = async (signal: AbortSignal) => {
    try {
      const response = await getData(
        `${apiEndpoints.app.users}/object?class_name=${encodeURIComponent(selectedClassName())}&project_id=${encodeURIComponent(selectedProjectId())}&user_id=${encodeURIComponent(user().user_id)}&parent_name=${parentName()}&object_name=${encodeURIComponent(objectName())}`,
        signal
      );
      if (!response.success) {
        if (response.type === "AbortError" || response.error === "Request cancelled") {
          return { chart_info: [], columns: 0, raw: null };
        }
        warn(`ProbabilityHistory: Failed to fetch chart configuration: ${response.error || response.message || "Unknown"}`);
        return { chart_info: [], columns: 0, raw: null };
      }
      if (!response.data?.chart_info?.length) {
        return { chart_info: [], columns: 0, raw: response.data };
      }
      return {
        chart_info: response.data.chart_info,
        columns: response.data.columns || 0,
        raw: response.data,
      };
    } catch (err: unknown) {
      if (err.name === "AbortError") {return { chart_info: [], columns: 0, raw: null };}
      warn("ProbabilityHistory: Error fetching charts:", err?.message || err);
      return { chart_info: [], columns: 0, raw: null };
    }
  };

  function mapRowsToCharts(
    chartsData: unknown[],
    dataPerChart: Record<string, unknown>[][],
    _signal?: AbortSignal,
    explorePlanOverride?: ExploreResampleFetchPlan | null
  ): unknown[] {
    if (!chartsData?.length) {return [];}
    const exploreResampleFetchPlan =
      explorePlanOverride !== undefined
        ? explorePlanOverride ?? buildExploreResampleFetchPlan(chartsData)
        : buildExploreResampleFetchPlan(chartsData);
    const batchChannelRefs = collectChannelRefsFromCharts(chartsData);

    return chartsData.map((chart, chartIndex) => {
      const data = dataPerChart[chartIndex] ?? [];
      const processedChart = { ...chart };
      processedChart.series = (chart.series || []).map((series: unknown, seriesIndex: number) => {
        const processedSeries = { ...series };
        const twaChannelName = defaultChannelsStore.twaName();
        const twaChannelNameLower = twaChannelName.toLowerCase();
        const seriesKey = `${chartIndex}-${seriesIndex}`;

        const seriesData = data
          .map((row: Record<string, unknown>) => {
            const twaValue =
              row.Twa !== undefined
                ? row.Twa
                : row[twaChannelName] !== undefined
                  ? row[twaChannelName]
                  : row[twaChannelNameLower] !== undefined
                    ? row[twaChannelNameLower]
                    : row.twa !== undefined
                      ? row.twa
                      : row.TWA !== undefined
                        ? row.TWA
                        : undefined;

            let xRaw: unknown;
            if (series.xaxis?.name && !isChartTypeYAxis(series.xaxis.name)) {
              const xBase = stripFleetChannelSuffix(series.xaxis.name);
              if (isDatetimeLikeExploreAxis(xBase)) {
                xRaw =
                  getRowChannelValue(row, series.xaxis.name) ??
                  getRowChannelValue(row, xBase) ??
                  row.ts ??
                  row.Timestamp ??
                  row.Datetime;
              } else {
                const xResampleKey =
                  exploreResampleFetchPlan?.seriesXPlotKeyByChartSeries?.[seriesKey];
                const xKey =
                  xResampleKey ?? resolveAxisDataColumnKey(series.xaxis, batchChannelRefs);
                xRaw = getRowChannelValue(row, xKey);
              }
            }

            const xNum = toNumericAxis(xRaw);
            const rowDatetime =
              row.Datetime ??
              row.datetime ??
              row.timestamp ??
              row.start_time ??
              row.Start_time;
            const finalTwa =
              twaValue !== undefined
                ? twaValue
                : row[twaChannelName] ?? row[twaChannelNameLower] ?? row.Twa ?? row.twa;

            const gradeValue =
              row.Grade !== undefined
                ? row.Grade
                : row.grade !== undefined
                  ? row.grade
                  : row.GRADE !== undefined
                    ? row.GRADE
                    : undefined;

            const rowIdRaw = row.ID ?? row.id ?? row.event_id ?? row.event_ID ?? row.EVENT_ID;
            const rowId =
              rowIdRaw != null && Number.isFinite(Number(rowIdRaw)) ? Number(rowIdRaw) : undefined;

            return normalizeExploreHistoryPointFields(row, {
              Datetime: rowDatetime,
              ID: rowId,
              x: xNum,
              twaChannelName,
              twaValue: finalTwa,
              gradeValue,
              color: series.color || "#1f77b4",
            });
          })
          .filter((pt) => {
            const hasX = pt.x !== undefined && Number.isFinite(pt.x as number);
            const hasDatetime = pt.Datetime !== undefined && pt.Datetime !== null && String(pt.Datetime).trim() !== "";
            const twaVal = pt[twaChannelName] ?? pt.Twa;
            const hasTwa = twaVal !== undefined && twaVal !== null && !Number.isNaN(Number(twaVal));
            return hasX && hasDatetime && hasTwa;
          });

        processedSeries.data = seriesData;
        processedSeries.originalData = [...seriesData];
        return processedSeries;
      });
      return processedChart;
    });
  }

  const fetchAndFormatData = async (
    chartsData: unknown[],
    mode: ProbabilityHistoryDataMode,
    rawSaved: Record<string, unknown> | null,
    fileResample: TimeseriesDataResample,
    signal?: AbortSignal
  ): Promise<unknown[]> => {
    if (!chartsData?.length) {return [];}

    // Live mode pins the range to the live session date; the active playback window is applied client-side below.
    const live = isLive();
    const liveDate = live ? normalizeApiDate(getLiveQueryDateYmd(selectedDate)) : null;
    // Fleet day mode pins both ends to the day being viewed (it has no date-range picker).
    const dayDate = isFleetDay() ? normalizeApiDate(selectedDate()) : null;
    const sd = live ? liveDate! : (dayDate ?? startDate());
    const ed = live ? liveDate! : (dayDate ?? endDate());
    if (!sd || !ed) {
      warn("ProbabilityHistory: start and end date are required");
      return [];
    }

    // Snapshot reactive values that must remain stable across the async await chain.
    const snapshotSourceNames = selectedSourceNamesForApi();
    const snapshotFilters = buildMetadataFilters();
    const snapshotFileMetaFilters = {
      year: splitMetadataFilterTokens(filterYear()),
      event: splitMetadataFilterTokens(filterEvent()),
      config: splitMetadataFilterTokens(filterConfig()),
      foilingStates: filterFoilingStates(),
    };

    const resampleMode = normalizeTimeseriesDataResample(fileResample);
    let fileExplorePlan: ExploreResampleFetchPlan | null = null;

    const requiredChannelsSet = new Set<string>();
    const filterChannels = await UnifiedFilterService.getRequiredFilterChannels(selectedClassName(), "fleet");
    filterChannels.forEach((c) => requiredChannelsSet.add(c));
    requiredChannelsSet.add("Datetime");
    requiredChannelsSet.add(defaultChannelsStore.twaName());
    UnifiedFilterService.getOptionalFilterChannels().forEach((c) => requiredChannelsSet.add(c));
    if (mode === "server_file") {
      // Per-sample channels needed for client-side Config / foiling-State filtering.
      requiredChannelsSet.add("Config_code");
      requiredChannelsSet.add("Foiling_state");
    }
    chartsData.forEach((chart) => {
      chart.series?.forEach((series: unknown) => {
        if (series.xaxis?.name && !isChartTypeYAxis(series.xaxis.name)) {
          requiredChannelsSet.add(series.xaxis.name.split(" - ")[0]);
        }
      });
    });
    const requiredChannels = Array.from(requiredChannelsSet);
    const batchChannelRefs = collectChannelRefsFromCharts(chartsData);

    let flatData: Record<string, unknown>[] = [];
    let dataPerChart: Record<string, unknown>[][] = [];

    if (mode === "server_file") {
      const chartsForFile = applyUniformDataResampleToCharts(chartsData, resampleMode);
      fileExplorePlan = buildExploreResampleFetchPlan(chartsForFile);
      const allowed = selectedSourceIds();
      progress.setProgress(10, "Loading file data...");
      flatData = await fetchProbabilityHistoryFileRows({
        className: selectedClassName(),
        projectId: Number(selectedProjectId()),
        startDate: normalizeApiDate(sd),
        endDate: normalizeApiDate(ed),
        allowedSourceIds: allowed.size > 0 ? allowed : new Set(),
        requiredChannels,
        channelRefs: batchChannelRefs.length > 0 ? batchChannelRefs : undefined,
        objectNameBase: `${objectName()}_hist`,
        exploreResampleFetchPlan: fileExplorePlan,
        onProgress: (p, m) => progress.setProgress(p, m),
        signal,
      });
      // Live mode: restrict to the active playback window (ending at the live edge) before display.
      let displayRows = flatData;
      if (live) {
        const win = computeMapActiveTimeWindow(true, timeWindow(), new Date(getLiveDisplayTargetMs()));
        if (win) {
          displayRows = filterRowsToLiveActiveWindow(flatData, win.start.getTime(), win.end.getTime());
        }
      }
      // Year/Event/Config/foiling-State filtered client-side for raw file rows. flatData stays
      // unfiltered so race/grade/leg option extraction below still sees the full dataset.
      const filteredFile = applyScatterHistoryMetadataFilters(displayRows, snapshotFileMetaFilters);
      dataPerChart = chartsData.map(() => filteredFile);
    } else if (mode === "dataset_aggregates") {
      const ev = (rawSaved?.aggregateEventType as string) || savedAggregateEventType();
      const legacyAgr = (rawSaved?.aggregateAgrType as string) || savedAggregateAgrType();
      progress.setProgress(20, "Loading aggregates...");
      const chans = requiredChannels.filter((c) => c !== "Datetime");
      const channelList = chans.length ? chans : ["Bsp", "Tws"];
      const agrPerChart = chartsData.map((ch: unknown) => {
        const a = ch?.aggregateAgrType;
        return typeof a === "string" && a.trim() ? a.trim().toLowerCase() : String(legacyAgr).toLowerCase();
      });
      const uniqueAgr = [...new Set(agrPerChart)];
      const rowsByAgr = new Map<string, Record<string, unknown>[]>();
      for (const agr of uniqueAgr) {
        if (signal?.aborted) {return [];}
        const rows = (await fetchProbabilityHistoryAggregatesAll({
          className: selectedClassName(),
          projectId: selectedProjectId(),
          startDate: sd,
          endDate: ed,
          eventType: ev,
          agrType: agr,
          channels: channelList,
          sourceNames: snapshotSourceNames,
          filters: snapshotFilters,
          signal,
        })) as Record<string, unknown>[];
        rowsByAgr.set(agr, rows);
      }
      dataPerChart = agrPerChart.map((a) => rowsByAgr.get(a) ?? []);
      flatData = dataPerChart.flat();
    } else if (mode === "start_stats") {
      progress.setProgress(20, "Loading start stats...");
      const chans = requiredChannels.filter((c) => c !== "Datetime");
      const channelList = chans.length ? chans : ["Prestart_dist", "Accel_max"];
      const rows = (await fetchProbabilityHistoryStartStatsAll({
        className: selectedClassName(),
        projectId: selectedProjectId(),
        startDate: sd,
        endDate: ed,
        channels: channelList,
        sourceNames: snapshotSourceNames,
        filters: snapshotFilters,
        signal,
      })) as Record<string, unknown>[];
      dataPerChart = chartsData.map(() => rows);
      flatData = rows;
    } else {
      const legacyMan = (rawSaved?.maneuverEventType as string) || savedManeuverEventType();
      progress.setProgress(20, "Loading maneuvers...");
      const chans = requiredChannels.filter((c) => c !== "Datetime");
      const channelList = chans.length ? chans : ["Tws_avg", "Bsp_min"];
      const manPerChart = chartsData.map((ch: unknown) => {
        const m = ch?.maneuverEventType;
        return typeof m === "string" && m.trim() ? m.trim().toLowerCase() : String(legacyMan).toLowerCase();
      });
      const uniqueMan = [...new Set(manPerChart)];
      const rowsByMan = new Map<string, Record<string, unknown>[]>();
      for (const eventT of uniqueMan) {
        if (signal?.aborted) {return [];}
        const rows = (await fetchProbabilityHistoryManeuversAll({
          className: selectedClassName(),
          projectId: selectedProjectId(),
          startDate: sd,
          endDate: ed,
          eventType: eventT,
          channels: channelList,
          sourceNames: snapshotSourceNames,
          filters: snapshotFilters,
          signal,
        })) as Record<string, unknown>[];
        rowsByMan.set(eventT, rows);
      }
      dataPerChart = manPerChart.map((t) => rowsByMan.get(t) ?? []);
      flatData = dataPerChart.flat();
    }

    if (signal?.aborted) {return [];}

    try {
      await extractFilterOptions(flatData as unknown[], "fleet");
      const opts = await unifiedDataStore.getFilterOptions();
      if (opts) {
        setRaceOptions((opts.races || []).slice().sort((a, b) => a - b));
        if (mode === "maneuver_stats" || mode === "start_stats") {
          setGradeOptions([-1, 1, 2]);
        } else {
          setGradeOptions((opts.grades || []).slice().sort((a, b) => a - b));
        }
        setLegOptions((opts.legs || opts.legOptions || []).slice().sort((a, b) => a - b));
        window.__raceToLegs = opts.raceToLegs || {};
      } else if (mode === "maneuver_stats" || mode === "start_stats") {
        setGradeOptions([-1, 1, 2]);
      }
    } catch (e) {
      debug("ProbabilityHistory: filter options", e);
      if (mode === "maneuver_stats" || mode === "start_stats") {
        setGradeOptions([-1, 1, 2]);
      }
    }

    progress.setProgress(90, "Building chart...");
    return mapRowsToCharts(
      chartsData,
      dataPerChart,
      signal,
      mode === "server_file" ? (fileExplorePlan ?? undefined) : undefined
    );
  };

  const hasChartFilters = (chartsToCheck: unknown[]) => {
    if (!chartsToCheck?.length) {return false;}
    const loaded = chartsToCheck.filter((c) => c?.series?.length);
    return loaded.some((c) => c.filters?.length > 0);
  };

  const applyFiltersToCharts = (
    currentCharts: unknown[],
    globalFilters = selectedStatesTimeseries(),
    globalRaces = selectedRacesTimeseries(),
    globalLegs = selectedLegsTimeseries(),
    globalGrades = selectedGradesTimeseries(),
    mode = dataMode()
  ) => {
    return currentCharts.map((chart) => ({
      ...chart,
      series: chart.series.map((series: unknown) => {
        const originalData = series.originalData || series.data;
        if (!series.originalData) {series.originalData = series.data;}
        let filteredData;
        if (chart.filters?.length > 0) {
          filteredData = applyScatterHistoryChartFilters(originalData, chart.filters);
        } else if (mode === "maneuver_stats" || mode === "start_stats") {
          filteredData = applyScatterHistoryGlobalManeuverFilters(originalData, {
            grades: globalGrades,
            // start_stats has no maneuver entry side; pass null so the entry filter no-ops.
            tackEntry: mode === "maneuver_stats" ? maneuverTackEntry() : null,
            races: globalRaces,
            legs: globalLegs,
          });
        } else {
          filteredData = applyDataFilter(originalData, globalFilters, globalRaces, globalLegs, globalGrades);
        }
        if (isCut() && cutEvents().length > 0) {
          filteredData = applyScatterHistorySelectionCutFilter(filteredData, cutEvents());
        }
        return { ...series, data: filteredData };
      }),
    }));
  };

  const syncSelectionStoreFromPointIds = (ids: Set<number>) => {
    if (ids.size === 0) {return;}
    const orig = originalCharts();
    const datetimes: Date[] = [];
    for (const chart of orig) {
      for (const series of chart.series || []) {
        const points = series.originalData || series.data || [];
        for (const pt of points) {
          if (ids.has(Number(pt.ID)) && pt.Datetime) {
            const dt = pt.Datetime instanceof Date ? pt.Datetime : new Date(pt.Datetime);
            if (!isNaN(dt.getTime())) {datetimes.push(dt);}
          }
        }
      }
    }
    if (datetimes.length === 0) {return;}
    datetimes.sort((a, b) => a.getTime() - b.getTime());
    setSelectedRange([
      {
        start_time: datetimes[0]?.toISOString(),
        end_time: datetimes[datetimes.length - 1]?.toISOString(),
      },
    ]);
    setHasSelection(true);
    if (cutPointIds().size === 0) {
      setIsCut(false);
    }
  };

  const handlePointSelectionChange = (ids: Set<number>) => {
    const cut = cutPointIds();
    const effectiveIds =
      cut.size > 0 ? new Set([...ids].filter((id) => cut.has(id))) : ids;
    syncSelectionStoreFromPointIds(effectiveIds);
    setSharedSelectedPointIds(effectiveIds);
    setLegendHighlight(null);
    setActiveLegendLabels([]);
  };

  const handleClearPointSelection = () => {
    runWithScatterSelectionBusyCursor(() => {
      const hadLassoSelection = sharedSelectedPointIds().size > 0;
      const cutActive = cutPointIds().size > 0;

      setSharedSelectedPointIds(new Set());

      if (hadLassoSelection && cutActive) {
        batch(() => {
          setSelectedRange([], { force: true });
          setSelectedRanges([]);
          setHasSelection(false);
        });
        return;
      }

      setCutPointIds(new Set());
      setLegendHighlight(null);
      setActiveLegendLabels([]);
      clearSelection();
      const orig = originalCharts();
      if (orig.length > 0) {
        setCharts(applyFiltersToCharts(orig));
      }
    });
  };

  const handleCutPointSelection = () => {
    const ids = sharedSelectedPointIds();
    if (ids.size === 0 && !hasSelection()) {return;}

    runWithScatterSelectionBusyCursor(() => {
    if (ids.size > 0) {
      const orig = originalCharts();
      const ranges = collectDatetimeRangesFromPointIds(ids, orig);
      if (ranges.length > 0) {
        setSelectedRanges(ranges);
        const datetimes = ranges
          .map((r) => new Date(r.start_time!))
          .filter((d) => !isNaN(d.getTime()))
          .sort((a, b) => a.getTime() - b.getTime());
        if (datetimes.length > 0) {
          setSelectedRange([
            {
              start_time: datetimes[0]?.toISOString(),
              end_time: datetimes[datetimes.length - 1]?.toISOString(),
            },
          ]);
          setHasSelection(true);
        }
      }
      setCutPointIds(new Set(ids));
    }

    cutSelection();
    setSharedSelectedPointIds(new Set());
    setLegendHighlight(null);
    setActiveLegendLabels([]);
    const orig = originalCharts();
    if (orig.length > 0) {
      setCharts(applyFiltersToCharts(orig));
    }
    });
  };

  const initializeCharts = async () => {
    const expectedPage = objectName();
    if (abortController) {abortController.abort();}
    abortController = new AbortController();
    const signal = abortController.signal;
    const myGeneration = ++initGeneration;

    setIsInitializing(true);
    unifiedDataStore.setLoading("probability", true);
    setChartsReadyCount(0);
    setHasChartConfigButNoData(false);
    setCharts([]);
    setOriginalCharts([]);

    const cn = selectedClassName();
    const pid = selectedProjectId();
    const sourcesReady = await ensureFleetSourcesContextReady({ className: cn, projectId: pid });
    if (!sourcesReady || signal.aborted) {
      if (myGeneration === initGeneration) {
        setIsInitializing(false);
        unifiedDataStore.setLoading("probability", false);
      }
      return;
    }

    try {
      const { chart_info: chartsData, columns: loadedColumns, raw } = await fetchCharts(signal);
      setColumns(loadedColumns);

      const r = raw as Record<string, unknown> | null;
      // Live and fleet-day copies always read raw parquet rows (server_file), never the SQL
      // aggregate/maneuver modes (their builders never write a dataMode).
      const mode = isLive() || isFleetDay() ? "server_file" : resolveProbabilityHistoryDataMode(r, chartsData);
      if (r?.dataMode !== mode) {
        debug("ProbabilityHistory: inferred dataMode (saved object missing or invalid dataMode)", {
          resolved: mode,
          saved: r?.dataMode ?? null,
          objectName: expectedPage,
        });
      }
      setDataMode(mode);
      if (typeof r?.aggregateEventType === "string") {setSavedAggregateEventType(r.aggregateEventType);}
      if (typeof r?.aggregateAgrType === "string") {setSavedAggregateAgrType(r.aggregateAgrType);}
      if (typeof r?.maneuverEventType === "string") {setSavedManeuverEventType(r.maneuverEventType);}

      if (expectedPage !== probabilityMetaAppliedForObject) {
        probabilityMetaAppliedForObject = expectedPage;
        if (r) {
          if (r.fileDataResample != null && String(r.fileDataResample).length > 0) {
            setFileDataResample(normalizeTimeseriesDataResample(r.fileDataResample));
          } else {
            setFileDataResample(DEFAULT_TIMESERIES_DATA_RESAMPLE);
          }
        } else {
          setFileDataResample(DEFAULT_TIMESERIES_DATA_RESAMPLE);
        }
      }

      // Defaults follow the datasets-window Year/Event filter (or last date − 4 days when ALL).
      if (!isLive() && !isFleetDay()) {
        await ensureScatterHistoryDefaultDateRange();
      }

      const updatedCharts = await fetchAndFormatData(chartsData, mode, r, fileDataResample(), signal);

      if (objectName() !== expectedPage) {return;}

      const hasConfig = (chartsData?.length ?? 0) > 0;
      const noChartsReturned = !updatedCharts?.length;
      const allEmpty =
        Array.isArray(updatedCharts) &&
        updatedCharts.length > 0 &&
        updatedCharts.every((ch: unknown) =>
          (ch.series || []).every((s: unknown) => !(s.data?.length > 0) && !(s.originalData?.length > 0))
        );
      const noData = hasConfig && (noChartsReturned || allEmpty);
      setHasChartConfigButNoData(noData);

      setOriginalCharts(updatedCharts);
      setCharts(applyFiltersToCharts(updatedCharts));
      if (!updatedCharts?.length || allEmpty) {
        progress.setProgress(100, "Complete");
      }
    } catch (err: unknown) {
      if (err.name !== "AbortError") {logError("ProbabilityHistory: initializeCharts", err);}
    } finally {
      // Only clear global loading state if this is still the latest init run.
      // An aborted (older) run must not clear the flag that a newer run just set.
      if (myGeneration === initGeneration) {
        setIsInitializing(false);
        unifiedDataStore.setLoading("probability", false);
      }
    }
  };

  const onChildChartReady = () => {
    const n = chartsReadyCount() + 1;
    setChartsReadyCount(n);
    if (n >= charts().length && charts().length > 0) {progress.setProgress(100, "Complete");}
  };

  const handleZoom = (info: unknown) => {
    const shouldExit =
      info == null ||
      (Array.isArray(info) && info.length === 0);
    if (shouldExit) {
      setSelectedChart(null);
      setZoom(false);
    } else {
      plotScrollRestore.captureBeforeEnteringPlotZoom();
      setSelectedChart(info);
      setZoom(true);
    }
  };

  createEffect(() => {
    if (!isInitializing() && charts().length > 0) {
      const loaded = charts().filter((c) => c?.series?.length);
      if (loaded.length > 0) {setHasChartsWithOwnFilters(hasChartFilters(charts()));}
    }
  });

  // Apply grade/race/leg/TWA filters live when the filter pills change. PageSettings only
  // calls onApply for date/source refetches, so without this effect toggling a filter pill
  // would never re-filter the charts.
  createEffect(() => {
    // Track the filter-pill signals for reactivity
    selectedStatesTimeseries();
    selectedRacesTimeseries();
    selectedLegsTimeseries();
    selectedGradesTimeseries();
    const orig = untrack(() => originalCharts());
    if (orig.length === 0 || isInitializing()) {return;}
    setCharts(applyFiltersToCharts(orig));
  });

  createEffect(() => {
    cutEvents();
    isCut();
    const orig = untrack(() => originalCharts());
    if (orig.length === 0 || isInitializing()) {return;}
    setCharts(applyFiltersToCharts(orig));
  });

  createEffect(() => {
    if (!filterChartsBySelection()) {return;}
    const range = selectedRange();
    const ranges = selectedRanges();
    const cuts = cutEvents();
    const hasSel = hasSelection();
    const cut = isCut();
    const selectionKey = JSON.stringify({
      range,
      ranges,
      cuts,
      hasSel,
      cut,
    });
    if (lastSelectionFilterKey === selectionKey) {return;}
    lastSelectionFilterKey = selectionKey;
    const orig = untrack(() => originalCharts());
    if (orig.length === 0 || isInitializing()) {return;}
    setCharts(applyFiltersToCharts(orig));
  });

  createEffect(() => {
    if (hasSelection() || selectedRange().length > 0) {return;}
    if (!isCut()) {
      if (sharedSelectedPointIds().size > 0) {
        setSharedSelectedPointIds(new Set());
      }
      if (cutPointIds().size > 0) {
        setCutPointIds(new Set());
      }
    }
  });

  const toggleRaceFilter = (race: number | string) => {
    const current = selectedRacesTimeseries();
    const raceStr = String(race);
    if (current.includes(raceStr)) {setSelectedRacesTimeseries(current.filter((r) => r !== raceStr));}
    else {setSelectedRacesTimeseries([...current, raceStr]);}
  };

  const toggleLegFilter = (leg: number) => {
    const current = selectedLegsTimeseries();
    const legStr = String(leg);
    if (current.includes(legStr)) {setSelectedLegsTimeseries(current.filter((l) => l !== legStr));}
    else {setSelectedLegsTimeseries([...current, legStr]);}
  };

  const toggleGradeFilter = (grade: number) => {
    const current = selectedGradesTimeseries();
    const gradeStr = String(grade);
    if (dataMode() === "maneuver_stats" || dataMode() === "start_stats") {
      // Grade is a single-select threshold for relational per-event modes.
      if (current.includes(gradeStr)) {
        setSelectedGradesTimeseries([]);
      } else {
        setSelectedGradesTimeseries([gradeStr]);
      }
      return;
    }
    if (current.includes(gradeStr)) {setSelectedGradesTimeseries(current.filter((g) => g !== gradeStr));}
    else {setSelectedGradesTimeseries([...current, gradeStr]);}
  };

  let cleanupKeyDown: (() => void) | undefined;

  const handleSelectionKeyDown = (event: KeyboardEvent): void => {
    const target = event.target as HTMLElement | null;
    if (
      target &&
      (target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable ||
        target.closest(".page-settings-modal"))
    ) {
      return;
    }
    if (document.querySelector(".pagesettings-modal")) {
      return;
    }

    if (event.key === "x" || event.key === "X") {
      if (sharedSelectedPointIds().size > 0 || hasSelection() || isCut()) {
        event.preventDefault();
        handleClearPointSelection();
      }
      return;
    }

    if (event.key === "c" || event.key === "C") {
      if (hasSelection() || sharedSelectedPointIds().size > 0) {
        event.preventDefault();
        handleCutPointSelection();
      }
    }
  };

  onMount(() => {
    const onBannerCleared = () => {
      setSharedSelectedPointIds(new Set());
      setCutPointIds(new Set());
      setLegendHighlight(null);
      setActiveLegendLabels([]);
    };
    const onBannerCut = (event: Event) => {
      event.preventDefault();
      handleCutPointSelection();
    };
    window.addEventListener("selection-banner-cleared", onBannerCleared);
    window.addEventListener("selection-banner-cut", onBannerCut);
    onCleanup(() => {
      window.removeEventListener("selection-banner-cleared", onBannerCleared);
      window.removeEventListener("selection-banner-cut", onBannerCut);
    });
  });

  onMount(() => {
    // Live and fleet day are reached from their own sidebar menus, which own selectedMenu.
    if (!isLive() && !isFleetDay()) {setSelectedMenu('PROBABILITY HISTORY');}
    void logPageLoad(
      "ProbabilityHistory.tsx",
      isLive() ? "Live Probability" : isFleetDay() ? "Fleet Probability" : "Probability History"
    );
    if (isLive()) {
      ensureLiveDatasetsForProject(Number(selectedProjectId()), selectedClassName()).catch(() => {});
    }
    // Show the overlay from the start; the effect below owns actually starting the work. There is
    // deliberately no `if (fleetSourcesReady()) initializeCharts()` fast-path here — having two
    // entry points is what produced the deadlock described on that effect.
    setIsInitializing(true);
    cleanupScaling = setupMediaContainerScaling({
      logPrefix: "ProbabilityHistory",
      scaleToWidth: true,
      getMediaContainer: () => mediaContainerRef ?? null,
    });
    window.addEventListener("keydown", handleSelectionKeyDown);
    cleanupKeyDown = () => window.removeEventListener("keydown", handleSelectionKeyDown);
  });

  let prevObjectName: string | undefined;

  /**
   * Start the initial load once the fleet source catalog is hydrated.
   *
   * THE DEADLOCK THIS REPLACES. The guard used to read
   * `if (!fleetSourcesReady() || isInitializing()) return;`, and `isInitializing` starts true and is
   * only ever cleared BY initializeCharts. On a cold mount fleetSourcesReady() is false, so the
   * onMount fast-path did not run either. The effect short-circuits on `!fleetSourcesReady()`, so it
   * only tracks readiness; when FleetSourcesGate hydrated it the effect re-ran, read isInitializing
   * — still true — and returned. Nothing writes that flag again, so the effect never fired again and
   * the page sat on "Loading... 0%" forever.
   *
   * The latch is a plain local, not a signal: it must not be readable as a dependency, or we are
   * back to gating the start on something the start itself changes.
   */
  let initStarted = false;
  createEffect(() => {
    if (!fleetSourcesReady()) {return;}
    if (initStarted || charts().length > 0) {return;}
    initStarted = true;
    void initializeCharts();
  });

  // Fleet day mode reads exactly one day, so a day change is a new query (the range picker that
  // would otherwise trigger this does not exist on that page).
  let prevFleetDayDate: string | undefined;
  createEffect(() => {
    if (!isFleetDay()) {return;}
    const d = normalizeApiDate(selectedDate());
    if (prevFleetDayDate !== undefined && prevFleetDayDate !== d) {
      void initializeCharts();
    }
    prevFleetDayDate = d;
  });

  let prevDatasetsFilterKey: string | undefined;
  createEffect(() => {
    if (isLive() || isFleetDay()) {return;}
    const key = `${String(selectedYear() || "ALL")}|${String(selectedEvent() || "ALL")}`;
    if (prevDatasetsFilterKey !== undefined && prevDatasetsFilterKey !== key) {
      void initializeCharts();
    }
    prevDatasetsFilterKey = key;
  });

  createEffect(() => {
    const name = objectName();
    if (prevObjectName !== undefined && prevObjectName !== name) {
      setCharts([]);
      setOriginalCharts([]);
      setChartsReadyCount(0);
      setSharedSelectedPointIds(new Set());
      setCutPointIds(new Set());
      untrack(() => clearSelection());
      void initializeCharts();
    }
    prevObjectName = name;
  });

  onCleanup(() => {
    setIsInitializing(false);
    abortController?.abort();
    cleanupScaling?.();
    cleanupKeyDown?.();
  });

  const useUPlotForMode = () =>
    dataMode() === "server_file" &&
    exploreChartsNeedUPlotRenderer([{ series: [{ dataResample: fileDataResample() }] }]);

  const colorLegendItems = createMemo(() =>
    buildScatterHistoryColorLegendItems(charts(), colorType())
  );

  const [legendHighlight, setLegendHighlight] = createSignal<ScatterLegendHighlightState | null>(null);
  const [activeLegendLabels, setActiveLegendLabels] = createSignal<string[]>([]);

  let prevLegendColorType: string | undefined;
  createEffect(() => {
    const ct = colorType();
    if (prevLegendColorType !== undefined && prevLegendColorType !== ct) {
      if (activeLegendLabels().length > 0 || legendHighlight() != null) {
        batch(() => {
          setLegendHighlight(null);
          setActiveLegendLabels([]);
          setSharedSelectedPointIds(new Set<number>());
          clearSelection();
        });
      }
    }
    prevLegendColorType = ct;
  });

  const zoomedChartLegendLabel = createMemo(() => {
    const chart = selectedChart();
    if (!chart) {return undefined;}
    const idx = charts().findIndex((c) => c === chart);
    return getChartLegendLabelForScatterHistory(chart, idx >= 0 ? idx : 0);
  });

  const handleLegendClick = (label: string): void => {
    if (isCut()) {return;}

    const ct = colorType();
    const twa = defaultChannelsStore.twaName();
    const sourceCharts = untrack(() => {
      const orig = originalCharts();
      return orig.length > 0 ? orig : charts();
    });
    const ids = collectScatterPointIdsForLegendClick(sourceCharts, ct, label, twa);
    if (ids.size === 0) {
      const already = legendHighlight()?.label === label && legendHighlight()?.colorType === ct;
      batch(() => {
        setLegendHighlight(already ? null : { colorType: ct, label });
        setActiveLegendLabels(already ? [] : [label]);
      });
      return;
    }

    const next = toggleIdsInSet(sharedSelectedPointIds(), ids);
    const groupSelected = [...ids].every((id) => next.has(id));
    batch(() => {
      setSharedSelectedPointIds(next);
      syncSelectionStoreFromPointIds(next);
      setActiveLegendLabels((prev) => {
        const has = prev.includes(label);
        if (groupSelected && !has) {return [...prev, label];}
        if (!groupSelected && has) {return prev.filter((l) => l !== label);}
        return prev;
      });
      setLegendHighlight(groupSelected ? { colorType: ct, label } : null);
      if (next.size === 0) {
        setHasSelection(false);
      }
    });
  };

  return (
    <>
      <div
        id="media-container"
        class={`scatter-page explore-scatter-page${hasChartConfigButNoData() ? " scatter-history-page--no-chart-data" : ""}`}
        ref={(el) => { mediaContainerRef = el as HTMLElement; }}
      >
        <FleetSourcesGate message="Loading fleet sources...">
        <div class="scatter-history-toolbar-icons settings-parent">
          <Show when={isLive()}>
            <button
              type="button"
              class="scatter-history-refresh-btn bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-1 px-3 rounded"
              title="Reload the latest live data for the active window"
              onClick={() => void initializeCharts()}
            >
              Refresh
            </button>
          </Show>
          <ProbabilityHistoryPageSettings
            useIconTrigger={true}
            options={twaFilterOptions}
            colorOptions={colortypes()}
            dataSourcesOptions={dataSourcesOptions()}
            selectedStates={selectedStatesTimeseries()}
            setSelectedStates={setSelectedStatesTimeseries}
            raceOptions={raceOptions()}
            setRaceOptions={setRaceOptions}
            legOptions={legOptions()}
            setLegOptions={setLegOptions}
            gradeOptions={gradeOptions()}
            setGradeOptions={setGradeOptions}
            selectedRaces={selectedRacesTimeseries().map((r) => {
              const num = Number(r);
              return Number.isNaN(num) ? r : num;
            })}
            selectedLegs={selectedLegsTimeseries().map((l) => Number(l)).filter((l) => !Number.isNaN(l))}
            selectedGrades={selectedGradesTimeseries().map((g) => Number(g)).filter((g) => !Number.isNaN(g))}
            onApply={() => void initializeCharts()}
            toggleFilter={(_groupIndex, _chartIndex, filter) => {
              const currentFilters = selectedStatesTimeseries();
              const newFilters = currentFilters.includes(filter)
                ? currentFilters.filter((f) => f !== filter)
                : [...currentFilters, filter];
              batch(() => {
                setSelectedStatesTimeseries(newFilters);
              });
            }}
            toggleRaceFilter={toggleRaceFilter}
            toggleLegFilter={toggleLegFilter}
            toggleGradeFilter={toggleGradeFilter}
            filterConfig={{
              showGrades: true,
              showTWA: dataMode() !== "maneuver_stats" && dataMode() !== "start_stats",
              showRaces: true,
              showLegs: true,
              showPhases: false,
              showPeriods: false,
              showBins: false,
              showHeadsail: false,
              showMainsail: false,
              showConfiguration: false,
            }}
            maneuverFilterMode={dataMode() === "maneuver_stats"}
            maneuverTackEntry={maneuverTackEntry}
            onManeuverTackEntryChange={setManeuverTackEntry}
            builderRoute={builderRoute()}
            builderNavigateTarget={builderTarget}
            {...(isLive()
              ? {}
              : {
                  // Year/Event/Config + foiling-State metadata filters (fleet day keeps them: they
                  // filter the day's raw rows client-side). The date-range picker is history-only.
                  metadataFilters: {
                    getYear: filterYear,
                    getEvent: filterEvent,
                    getConfig: filterConfig,
                    setYear: setFilterYear,
                    setEvent: setFilterEvent,
                    setConfig: setFilterConfig,
                    getSourceNames: () => selectedSourceNamesForApi().map((n) => n.toLowerCase()),
                  },
                  foilingState: {
                    options: FOILING_STATE_OPTIONS,
                    getSelected: filterFoilingStates,
                    setSelected: setFilterFoilingStates,
                  },
                  ...(isFleetDay()
                    ? {}
                    : {
                        scatterHistory: {
                          getStartDate: startDate,
                          getEndDate: endDate,
                          setStartDate,
                          setEndDate,
                        },
                      }),
                })}
          />
        </div>

        <div class="container relative">
          <div class="performance-charts-scroll-container">
            <div
              class="performance-charts-scroll-inner relative"
              ref={(el) => {
                performanceChartsScrollInnerEl = el as HTMLElement;
              }}
            >
              <Show
                when={charts().length > 0 && !hasChartConfigButNoData()}
                fallback={
                  <Show when={!isInitializing() && !unifiedDataStore.getLoading("probability")}>
                    <Show
                      when={hasChartConfigButNoData()}
                      fallback={
                        <div class="flex flex-col items-center justify-center h-screen min-h-[500px] text-center p-8">
                          <h3 class="text-xl font-semibold mb-2">No charts available</h3>
                          <p class="text-gray-500 mb-6">Add a chart in the builder.</p>
                          <button
                            type="button"
                            class="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg"
                            onClick={() => navigate(builderTarget())}
                          >
                            Open builder
                          </button>
                        </div>
                      }
                    >
                      <div class="scatter-history-empty-no-data" role="status" aria-label="No chart data">
                        <DataNotFoundMessage
                          builderRoute={builderRoute()}
                          onNavigateToBuilder={() => navigate(builderTarget())}
                        />
                      </div>
                    </Show>
                  </Show>
                }
              >
                <Show when={!zoom()}>
                  <ScatterHistoryColorLegend
                    colorType={colorType()}
                    items={colorLegendItems()}
                    activeLabels={activeLegendLabels()}
                    onItemClick={handleLegendClick}
                  />
                  <div
                    class={layoutClass()}
                    style={{ "padding-top": gridPaddingTop() }}
                  >
                    <For each={charts()}>
                      {(chart) => {
                        const ChartComponent = getChartComponent(chart);
                        return (
                          <ChartComponent
                            chart={chart}
                            colortype={colorType()}
                            class_name={chartLayoutClass()}
                            handleZoom={handleZoom}
                            zoom={zoom()}
                            onReady={onChildChartReady}
                            scatterHistoryDataMode={dataMode()}
                            maneuverTackEntry={maneuverTackEntry}
                          />
                        );
                      }}
                    </For>
                  </div>
                </Show>
                <Show when={zoom()}>
                  <div class="zoom-container min-h-[800px] mt-[50px]">
                    <div class="flex w-full h-full h-[800px] w-[calc(100%-25px)]">
                      {(() => {
                        const chart = selectedChart();
                        if (!chart) {return null;}
                        const ChartComponent = getChartComponent(chart);
                        return (
                          <ChartComponent
                            chart={chart}
                            colortype={colorType()}
                            class_name={chartLayoutClass()}
                            handleZoom={handleZoom}
                            zoom={zoom()}
                            onReady={onChildChartReady}
                            scatterHistoryDataMode={dataMode()}
                            maneuverTackEntry={maneuverTackEntry}
                          />
                        );
                      })()}
                    </div>
                  </div>
                </Show>
              </Show>
            </div>
          </div>
        </div>
        </FleetSourcesGate>
      </div>

      <Show
        when={
          isInitializing() ||
          unifiedDataStore.getLoading("probability") ||
          (!hasChartConfigButNoData() &&
            (progress.isLoading() ||
              (!zoom() && charts().length > 0 && chartsReadyCount() < charts().length)))
        }
      >
        <LoadingOverlay
          message={progress.message()}
          fullScreen={true}
          showProgress={true}
          progress={progress.progress()}
          progressMessage={progress.message()}
        />
      </Show>
    </>
  );
}
